import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { CartProvider } from "./context/CartContext";
import { MetaPixelRouteTracker } from "./lib/metaPixel";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import DisclaimerBanner from "./components/DisclaimerBanner";
import { LiveDataBanner } from "./components/LiveDataBanner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import EscapeToClose from "./components/EscapeToClose";
import ModalPrimaryHotkey from "./components/ModalPrimaryHotkey";
import { ADMIN_BASE } from "./lib/adminPath";

// Deferred global chunks — invisible-until-triggered helpers (cart
// drawer, toasts, PWA prompts, admin hotkeys) load right after first
// paint instead of inside the critical main bundle.
const GlobalOverlays = lazy(() => import("./components/GlobalOverlays"));
const AdminOverlays = lazy(() => import("./components/AdminOverlays"));

import Home from "./pages/Home";
import Shop from "./pages/Shop";

// ProductDetail is lazy too — it pulls react-markdown (product
// descriptions) which would otherwise anchor ~40 KB gz in the main
// bundle that the Home/Shop first paint never needs.
const ProductDetail = lazy(() => import("./pages/ProductDetail"));

// Route-based code splitting: everything below is lazy-loaded so
// first-time shoppers only download the storefront-critical bundle
// (Home/Shop/ProductDetail + shell). Admin pages, checkout follow-ups
// and legal pages fetch their chunks on navigation.
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const About = lazy(() => import("./pages/About"));
const News = lazy(() => import("./pages/News"));
const ResearchOzempicVsRetatrutide = lazy(
  () => import("./pages/ResearchOzempicVsRetatrutide"),
);
const ResearchTriumphTrials = lazy(
  () => import("./pages/ResearchTriumphTrials"),
);
const ResearchIndex = lazy(() => import("./pages/ResearchIndex"));
const ResearchMenVsWomen = lazy(() => import("./pages/ResearchMenVsWomen"));
const ResearchSideEffects = lazy(() => import("./pages/ResearchSideEffects"));
const ResearchReconstitution = lazy(() => import("./pages/ResearchReconstitution"));
const PeptideCalculator = lazy(() => import("./pages/PeptideCalculator"));
const ResearchGlp3 = lazy(() => import("./pages/ResearchGlp3"));
const ResearchTirzVsReta = lazy(() => import("./pages/ResearchTirzVsReta"));
const ResearchApprovalTimeline = lazy(() => import("./pages/ResearchApprovalTimeline"));
const ResearchResultsTimeline = lazy(() => import("./pages/ResearchResultsTimeline"));
const FeedArticle = lazy(() => import("./pages/FeedArticle"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Tests = lazy(() => import("./pages/Tests"));
const LabResults = lazy(() => import("./pages/LabResults"));
const Reviews = lazy(() => import("./pages/Reviews"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Orders = lazy(() => import("./pages/Orders"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminProducts = lazy(() => import("./pages/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminStockAudit = lazy(() => import("./pages/AdminStockAudit"));
const AdminPromoCodes = lazy(() => import("./pages/AdminPromoCodes"));
const AdminShipping = lazy(() => import("./pages/AdminShipping"));
const AdminCatalogOrder = lazy(() => import("./pages/AdminCatalogOrder"));
const AdminInvoiceSettings = lazy(() => import("./pages/AdminInvoiceSettings"));
const AdminInvoices = lazy(() => import("./pages/AdminInvoices"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminPages = lazy(() => import("./pages/AdminPages"));
const AdminUpsellStats = lazy(() => import("./pages/AdminUpsellStats"));
const AdminReviews = lazy(() => import("./pages/AdminReviews"));
const AdminCategories = lazy(() => import("./pages/AdminCategories"));
const AdminPageSpeed = lazy(() => import("./pages/AdminPageSpeed"));
const AdminSeo = lazy(() => import("./pages/AdminSeo"));
const AdminArticles = lazy(() => import("./pages/AdminArticles"));
const AdminCrawlCheck = lazy(() => import("./pages/AdminCrawlCheck"));
const AdminGmail = lazy(() => import("./pages/AdminGmail"));
const AdminHolding = lazy(() => import("./pages/AdminHolding"));
const CartResume = lazy(() => import("./pages/CartResume"));
const EtransferConfirmation = lazy(() => import("./pages/EtransferConfirmation"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));

function RouteFallback() {
  return (
    <div
      data-testid="route-loading"
      className="flex items-center justify-center py-32"
    >
      <span className="h-6 w-6 rounded-full border-2 border-white/20 border-t-white/70 animate-spin" />
    </div>
  );
}

function App() {
  return (
    <div className="App min-h-screen flex flex-col bg-[#0a0a0a] text-[#F0F0EE]">
      <BrowserRouter>
        <ScrollToTop />
        <MetaPixelRouteTracker />
        <EscapeToClose />
        <ModalPrimaryHotkey />
        <AdminAuthProvider>
          <Suspense fallback={null}>
            <AdminOverlays />
          </Suspense>
          <CartProvider>
            <DisclaimerBanner />
            <LiveDataBanner />
            <Header />
            <main className="flex-1">
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/category/:slug" element={<Shop />} />
                <Route path="/shop/:id" element={<ProductDetail />} />
                <Route
                  path="/checkout/success"
                  element={<CheckoutSuccess />}
                />
                <Route path="/about" element={<About />} />
                <Route path="/news" element={<News />} />
                <Route
                  path="/research/ozempic-vs-retatrutide"
                  element={<ResearchOzempicVsRetatrutide />}
                />
                <Route
                  path="/research/retatrutide-triumph-trials"
                  element={<ResearchTriumphTrials />}
                />
                <Route path="/research" element={<ResearchIndex />} />
                <Route
                  path="/research/in-house"
                  element={<ResearchIndex variant="inhouse" />}
                />
                <Route
                  path="/research/other"
                  element={<ResearchIndex variant="feed" />}
                />
                <Route
                  path="/research/retatrutide-men-vs-women"
                  element={<ResearchMenVsWomen />}
                />
                <Route
                  path="/research/minimizing-retatrutide-side-effects"
                  element={<ResearchSideEffects />}
                />
                <Route
                  path="/research/retatrutide-reconstitution-guide"
                  element={<ResearchReconstitution />}
                />
                <Route
                  path="/tools/retatrutide-calculator"
                  element={<PeptideCalculator />}
                />
                <Route
                  path="/research/what-is-glp-3"
                  element={<ResearchGlp3 />}
                />
                <Route
                  path="/research/tirzepatide-vs-retatrutide"
                  element={<ResearchTirzVsReta />}
                />
                <Route
                  path="/research/feed/:id"
                  element={<FeedArticle />}
                />
                <Route
                  path="/research/retatrutide-approval-timeline"
                  element={<ResearchApprovalTimeline />}
                />
                <Route
                  path="/research/retatrutide-results-timeline"
                  element={<ResearchResultsTimeline />}
                />
                <Route
                  path="/research/:slug"
                  element={<FeedArticle />}
                />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/lab-results" element={<LabResults />} />
                <Route path="/lab-results/retatrutide" element={<Tests />} />
                <Route
                  path="/tests"
                  element={<Navigate to="/lab-results" replace />}
                />
                <Route path="/reviews" element={<Reviews />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfUse />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/cart" element={<CartResume />} />
                <Route
                  path="/checkout/etransfer/:session_id"
                  element={<EtransferConfirmation />}
                />
                {/* Admin pages live under a secret prefix from REACT_APP_ADMIN_PATH.
                    Anyone hitting the legacy /admin/* paths falls through to the
                    catch-all below which sends them to Home. */}
                <Route path={ADMIN_BASE} element={<AdminLogin />} />
                <Route path={`${ADMIN_BASE}/login`} element={<AdminLogin />} />
                <Route
                  path={`${ADMIN_BASE}/dashboard`}
                  element={<AdminDashboard />}
                />
                <Route
                  path={`${ADMIN_BASE}/products`}
                  element={<AdminProducts />}
                />
                <Route
                  path={`${ADMIN_BASE}/orders`}
                  element={<AdminOrders />}
                />
                <Route
                  path={`${ADMIN_BASE}/stock-audit`}
                  element={<AdminStockAudit />}
                />
                <Route
                  path={`${ADMIN_BASE}/promo-codes`}
                  element={<AdminPromoCodes />}
                />
                <Route
                  path={`${ADMIN_BASE}/shipping`}
                  element={<AdminShipping />}
                />
                <Route
                  path={`${ADMIN_BASE}/catalog-order`}
                  element={<AdminCatalogOrder />}
                />
                <Route
                  path={`${ADMIN_BASE}/invoice-emails`}
                  element={<AdminInvoiceSettings />}
                />
                <Route
                  path={`${ADMIN_BASE}/invoices`}
                  element={<AdminInvoices />}
                />
                <Route
                  path={`${ADMIN_BASE}/users`}
                  element={<AdminUsers />}
                />
                <Route
                  path={`${ADMIN_BASE}/pages`}
                  element={<AdminPages />}
                />
                <Route
                  path={`${ADMIN_BASE}/upsell-stats`}
                  element={<AdminUpsellStats />}
                />
                <Route
                  path={`${ADMIN_BASE}/reviews`}
                  element={<AdminReviews />}
                />
                <Route
                  path={`${ADMIN_BASE}/categories`}
                  element={<AdminCategories />}
                />
                <Route
                  path={`${ADMIN_BASE}/holding`}
                  element={<AdminHolding />}
                />
                <Route
                  path={`${ADMIN_BASE}/pagespeed`}
                  element={<AdminPageSpeed />}
                />
                <Route
                  path={`${ADMIN_BASE}/seo`}
                  element={<AdminSeo />}
                />
                <Route
                  path={`${ADMIN_BASE}/articles`}
                  element={<AdminArticles />}
                />
                <Route
                  path={`${ADMIN_BASE}/crawl-check`}
                  element={<AdminCrawlCheck />}
                />
                <Route
                  path={`${ADMIN_BASE}/gmail`}
                  element={<AdminGmail />}
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </main>
            <Footer />
            <Suspense fallback={null}>
              <GlobalOverlays />
            </Suspense>
          </CartProvider>
        </AdminAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
